import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "./data";
import { scrollToId, getLenis } from "../../lib/scroll";
import { EASE } from "./Motion";

const go = (e, id, after) => {
  e.preventDefault();
  if (after) after();
  scrollToId(id);
};

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: EASE }}
    className="fixed inset-0 z-[60] flex flex-col bg-ns-emerald px-6 pb-10 pt-6 grain"
    data-testid="mobile-menu-panel"
  >
    <div className="flex items-center justify-between">
      <Logo light testId="mobile-menu-logo" onClick={(e) => go(e, "home", onClose)} />
      <button onClick={onClose} data-testid="mobile-menu-close-button" aria-label="Close menu" className="grid h-12 w-12 place-items-center rounded-full border border-ns-gold/40 text-ns-ivory">
        <X className="h-5 w-5" />
      </button>
    </div>
    <nav className="mt-14 flex flex-col">
      {NAV.map((n, i) => (
        <motion.a
          key={n.id}
          href={`#${n.id}`}
          onClick={(e) => go(e, n.id, onClose)}
          data-testid={`mobile-${n.testId}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 + i * 0.06, duration: 0.6, ease: EASE }}
          className="flex items-baseline justify-between border-b border-ns-gold/15 py-4 font-serif text-4xl text-ns-ivory"
        >
          {n.label}
          <span className="font-sans text-xs tracking-widest text-ns-gold">0{i + 1}</span>
        </motion.a>
      ))}
    </nav>
    <p className="mt-auto font-hindi text-3xl text-ns-gold">स्वाद, सेहत और भरोसा भी</p>
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const l = getLenis();
    if (open) l?.stop();
    else l?.start();
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -90 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled ? "bg-ns-ivory/80 py-3 shadow-[0_1px_0_rgba(11,60,42,0.08)] backdrop-blur-xl" : "py-5"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Logo onClick={(e) => go(e, "home")} />
          <nav className="hidden items-center gap-7 xl:flex" data-testid="desktop-nav">
            {NAV.map((n) =>
              n.id === "distributor" ? (
                <a key={n.id} href={`#${n.id}`} onClick={(e) => go(e, n.id)} data-testid={n.testId} className="group inline-flex items-center gap-1.5 rounded-full bg-ns-emerald px-5 py-2.5 text-sm font-semibold text-ns-ivory transition-colors duration-300 hover:bg-ns-deep">
                  {n.label}
                  <ArrowUpRight className="h-4 w-4 text-ns-gold transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              ) : (
                <a key={n.id} href={`#${n.id}`} onClick={(e) => go(e, n.id)} data-testid={n.testId} className="link-underline text-sm font-medium text-ns-ink">
                  {n.label}
                </a>
              )
            )}
          </nav>
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu" className="grid h-12 w-12 place-items-center rounded-full bg-ns-emerald text-ns-ivory xl:hidden">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </motion.header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
